import { motion } from "framer-motion";
import { TrendingUp, Users, DollarSign, Eye } from "lucide-react";

const caseStudies = [
  {
    id: 1,
    client: "Archimat",
    industry: "Architecture / Showroom",
    services: ["Social Media Marketing", "Content Production", "Paid Advertising"],
    heroStat: "1.6M",
    heroLabel: "IMPRESSIONS IN 2 MONTHS",
    stats: [
      { icon: Eye, value: "1.6M", label: "Impressions" },
      { icon: Users, value: "2x", label: "Follower Growth" },
    ],
    story:
      "TAKEOFF took over Archimat's social media presence and turned it into their most powerful sales channel. Within two months, we generated 1.6 million impressions and more than doubled their following. The impact went far beyond vanity metrics — the owner had to hire additional staff to handle the surge of walk-in traffic to their showroom, all driven by social media.",
    quote: "I didn't expect this kind of impact from social media. These guys are experts they really know what they're doing. We went from barely having a presence online to getting so much traffic in the showroom that I had to hire more people just to keep up with the demand. All of it coming from social media. I recommend them to any business owner.",
    quoteAuthor: "Charles Nassif, Owner",
    image: "https://customer-assets.emergentagent.com/job_automate-scale-4/artifacts/9zvd6btz_ARCHIMAT.png",
  },
  {
    id: 2,
    client: "Groupe JSV",
    industry: "Tool Distribution",
    services: ["Social Media Marketing", "Content Production", "Paid Advertising"],
    heroStat: "$40K",
    heroLabel: "REVENUE DRIVEN VIA SOCIAL",
    stats: [
      { icon: DollarSign, value: "$40K", label: "Tracked Revenue" },
      { icon: Users, value: "2x", label: "Social Following" },
    ],
    story:
      "Groupe JSV's Marketing Director was overloaded — so TAKEOFF stepped in and took over the entire content strategy, production, and paid advertising. We managed their brand ambassador Sansdrick Lavoie, created a series of commercials, and ran a giveaway campaign that doubled their social following. The result? $40,000 in trackable revenue driven directly to their new website through social media.",
    quote: "I was handling everything on my own, content, ads, posting and it was too much. TAKEOFF came in and took it all off my plate. They didn't just manage it, they elevated it. The ambassador campaign, the commercials, the giveaway, everything was executed at a high level. We drove real revenue to our new website and doubled our following. These guys operate like an extension to our in-house team.",
    quoteAuthor: "Domenico Sciascia, Marketing Director",
    image: "https://customer-assets.emergentagent.com/job_automate-scale-4/artifacts/zqkovlhk_GROUPE%20JSV.png",
  },
  {
    id: 3,
    client: "Dolce Sapore",
    industry: "Restaurant / Food & Beverage",
    services: ["Social Media Marketing", "Content Production", "Paid Advertising"],
    heroStat: "15-20%",
    heroLabel: "REVENUE INCREASE YoY",
    stats: [
      { icon: Eye, value: "1.4M", label: "Impressions in 2 Months" },
      { icon: TrendingUp, value: "15-20%", label: "Revenue Growth" },
      { icon: DollarSign, value: "5-Fig", label: "Ad Spend Managed" },
    ],
    story:
      "TAKEOFF manages Dolce Sapore's social media end-to-end — from content strategy and creation to paid advertising. In just two months, we generated 1.4 million impressions. Over the course of the year, we managed a 5-figure ad budget and helped increase their revenue by 15–20% compared to the previous year.",
    quote: "We were posting stuff on Instagram but honestly it wasn't doing much. TAKEOFF came in and completely changed the game for us. Now people come in and tell me they saw us on social media, that never used to happen. The ads are working, the content looks professional, and our revenue went up compared to last year. I don't even think about social media anymore because I know they're handling it.",
    quoteAuthor: "Alessio Muro, Co-Owner",
    image: "https://customer-assets.emergentagent.com/job_automate-scale-4/artifacts/rtp6po0e_DOLCE%20SAPORE.png",
  },
  {
    id: 4,
    client: "DGYM",
    industry: "Fitness / Digital Products",
    services: ["Website Development", "Video Production", "Social Media Marketing", "Content Creation"],
    heroStat: "$500K+",
    heroLabel: "REVENUE IN 12 MONTHS",
    stats: [
      { icon: DollarSign, value: "$500K+", label: "Revenue Generated" },
      { icon: Users, value: "2,000+", label: "Active Members" },
      { icon: TrendingUp, value: "High", label: "Retention Rate" },
    ],
    story:
      "TAKEOFF built DGYM's entire digital ecosystem from the ground up. We developed dgymtraining.com, a virtual 20-minute home workout program. We filmed every single workout, and created social media content used to market and sell it. Within just over 12 months of going live, the platform generated over $500K in revenue and grew to 2,000+ members with an exceptional retention rate. We continue to level up the program with fresh workouts every 8 weeks, ongoing website upgrades, and paid ads launching in Q2 2026.",
    quote: "I came to TAKEOFF with an idea and they turned it into something way bigger than I imagined. I'm a trainer, not a tech guy so I needed someone who could handle the website, the filming, and some of the marketing stuff. A year later we have 2,000+ members and we keep leveling up the program every 8 weeks. It's incredible to have a team that understands the vision and is able to execute any idea I have.",
    quoteAuthor: "Derek Luciani, Owner",
    image: "https://customer-assets.emergentagent.com/job_automate-scale-4/artifacts/8fqtpnqe_DGYM.png",
  },
];

export default function CaseStudies() {
  return (
    <section
      data-testid="case-studies-section"
      id="work"
      className="py-24 md:py-32 bg-dark"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="case-studies-title"
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase text-white"
          >
            Case <span className="text-lime">Studies</span>
          </h2>
          <p className="font-body text-white/60 mt-4 max-w-xl mx-auto">
            Real results for real businesses. See how we've helped brands scale.
          </p>
        </motion.div>

        {/* Case Study Cards */}
        <div className="space-y-8">
          {caseStudies.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`case-study-${index}`}
              className="bg-dark-card border border-white/5 overflow-hidden hover:border-lime/30 transition-all duration-300"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image Side */}
                <div className="relative h-64 lg:h-auto min-h-[300px] overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.client}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-dark-card/90 via-dark-card/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-dark-card/30 lg:to-dark-card" />
                  
                  {/* Hero Stat Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:hidden">
                    <div className="font-heading font-extrabold text-4xl text-lime">
                      {study.heroStat}
                    </div>
                    <div className="font-body text-sm text-white/80 uppercase tracking-wide">
                      {study.heroLabel}
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                  {/* Hero Stat - Desktop */}
                  <div className="hidden lg:block mb-6">
                    <div className="font-heading font-extrabold text-5xl xl:text-6xl text-lime leading-none">
                      {study.heroStat}
                    </div>
                    <div className="font-body text-sm text-white/70 uppercase tracking-wider mt-2">
                      {study.heroLabel}
                    </div>
                  </div>

                  {/* Client & Industry */}
                  <div className="mb-4">
                    <h3 className="font-heading font-bold text-2xl md:text-3xl uppercase text-white">
                      {study.client}
                    </h3>
                    <p className="font-body text-sm text-white/50 mt-1">
                      {study.industry}
                    </p>
                  </div>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {study.services.map((service, idx) => (
                      <span
                        key={idx}
                        className="font-body text-xs px-3 py-1 bg-lime/10 text-lime border border-lime/20"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  {/* Stats Row */}
                  <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b border-white/10">
                    {study.stats.map((stat, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-dark border border-white/10 flex items-center justify-center">
                          <stat.icon className="w-5 h-5 text-lime" />
                        </div>
                        <div>
                          <div className="font-heading font-bold text-xl text-white">
                            {stat.value}
                          </div>
                          <div className="font-body text-xs text-white/50">
                            {stat.label}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Story */}
                  <p className="font-body text-sm text-white/70 leading-relaxed mb-6">
                    {study.story}
                  </p>

                  {/* Quote */}
                  {study.quote && (
                    <div className="bg-dark/50 border-l-2 border-lime pl-4 py-3">
                      <p className="font-body text-sm text-white/80 italic">
                        "{study.quote}"
                      </p>
                      {study.quoteAuthor && (
                        <p className="font-body text-xs text-lime mt-2">
                          — {study.quoteAuthor}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
